import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  BadgeTone,
  Box,
  Button,
  ButtonProps,
  ButtonTone,
  Card,
  Flex,
  Grid,
  Stack,
  Text
} from "@sanity/ui";
import { DashboardWidgetContainer } from "@sanity/dashboard";
import { GiDeathSkull, GiTransform } from "react-icons/gi";
import { MdPriceCheck } from "react-icons/md";
import { FaHotel, FaShip } from "react-icons/fa";
import { HttpError, useSanityImportApi } from "../commons/useSanityImportApi";

const buttonToneFromStatus = new Map<string | undefined, BadgeTone>([
  ["SUCCEEDED", "positive"],
  ["RUNNING", "caution"],
  ["FAILED", "critical"],
  ["ABORTED", "critical"],
  ["TIMED_OUT", "critical"],
  [undefined, "default"]
]);

function formatDate(date: Date | undefined | null) {
  return date?.toLocaleDateString("en-GB", {
    year: "2-digit",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
  });
}

type ImportButtonProps = ButtonProps & {
  disabled: boolean;
  text: string;
  description: string;
  onClick: () => void;
  tone?: ButtonTone;
};

const ImportButton: React.FC<ImportButtonProps> = ({
  description,
  disabled,
  onClick,
  text,
  tone = "positive",
  ...props
}) => {
  return (
    <Stack style={{ width: "100%" }}>
      <Button
        fontSize={[2, 2, 3]}
        padding={[3, 3, 4]}
        text={text}
        tone={tone}
        disabled={disabled}
        onClick={onClick}
        {...props}
      />

      <Box padding={2}>
        <Text muted size={1}>
          {description}
        </Text>
      </Box>
    </Stack>
  );
};

const InitiateImport: React.FC = () => {
  const sanityImportApiUrl = process.env.SANITY_STUDIO_SILVERSEA_API!;
  const [importType, setImportType] = useState<string>();
  const [pollingEnabled, setPollingEnabled] = useState(true);

  const {
    data: executions,
    isLoading: isLoadingExecutions,
    refetch,
    isError: isLastExecError,
    error: lastExecError
  } = useSanityImportApi.useLastExecution(sanityImportApiUrl, pollingEnabled);

  const {
    isLoading: isRunning,
    isSuccess,
    isError: isRunError,
    error: runError
  } = useSanityImportApi.useRunImport(sanityImportApiUrl, importType);

  useEffect(() => {
    const checkError = (error: unknown) => {
      if (
        error instanceof HttpError &&
        [400, 401, 403].includes(error.status)
      ) {
        setPollingEnabled(false);
      }
    };

    if (isLastExecError) checkError(lastExecError);
    if (isRunError) checkError(runError);
  }, [isLastExecError, lastExecError, isRunError, runError]);

  const handleRunImport = (type: string) => {
    setImportType(type);
  };
  const isSfnRunning = useMemo(() => {
    const importRunning = executions?.importShopApi.status === "RUNNING";
    const transformRunning = executions?.transformShopApi.status === "RUNNING";
    return importRunning || transformRunning;
  }, [executions?.importShopApi.status, executions?.transformShopApi.status]);

  const buttonDisabled =
    isLoadingExecutions ||
    isRunning ||
    isSfnRunning ||
    isLastExecError ||
    isRunError;

  useEffect(() => {
    if (isSuccess) {
      setImportType(undefined);
      void refetch();
    }
  }, [isSuccess, refetch]);

  return (
    <DashboardWidgetContainer
      //@ts-ignore
      header={
        <div>
          Shop Api Import
          <br /> <Badge tone={"caution"}>USE WITH CAUTION</Badge>
        </div>
      }
    >
      <Stack as={"ul"}>
        <Card borderBottom as={"li"} padding={4}>
          <Grid gridTemplateColumns={4}>
            <Box gridColumn={3}>
              <Card padding={4}>
                <Stack gap={3}>
                  <Text size={2}>Import</Text>

                  <Text muted size={1}>
                    {executions?.importShopApi.startDate
                      ? `Start: ${formatDate(executions.importShopApi.startDate)} Stop: ${formatDate(executions.importShopApi.stopDate) ?? ""}`
                      : "Never"}
                  </Text>
                </Stack>
              </Card>
            </Box>

            <Flex justify={"center"} align={"center"}>
              <Stack gap={3}>
                <Badge
                  tone={buttonToneFromStatus.get(
                    executions?.importShopApi.status ?? undefined
                  )}
                >
                  {executions?.importShopApi.status}
                </Badge>
              </Stack>
            </Flex>

            <Box gridColumn={3}>
              <Card padding={4}>
                <Stack gap={3}>
                  <Text size={2}>Transform</Text>

                  <Text muted size={1}>
                    {executions?.transformShopApi.startDate
                      ? `Start: ${formatDate(executions.transformShopApi.startDate)} Stop: ${formatDate(executions.transformShopApi.stopDate) ?? ""}`
                      : "Never"}
                  </Text>
                </Stack>
              </Card>
            </Box>

            <Flex justify={"center"} align={"center"}>
              <Stack gap={3}>
                <Badge
                  tone={buttonToneFromStatus.get(
                    executions?.transformShopApi.status ?? undefined
                  )}
                >
                  {executions?.transformShopApi.status}
                </Badge>
              </Stack>
            </Flex>
          </Grid>
          <hr />
          <Stack gap={4} paddingTop={4} paddingBottom={4}>
            <Text size={3}>Transform only</Text>
            <Grid gridTemplateColumns={2} gap={[1, 1, 2, 3]}>
              <ImportButton
                icon={GiTransform}
                text="Transform Only"
                description={`Use when an "Offer" or it's tokens change in Sanity`}
                disabled={buttonDisabled}
                onClick={() => handleRunImport("transformOnly")}
              />
            </Grid>
          </Stack>
          <hr />
          <Stack gap={4} paddingTop={4} paddingBottom={4}>
            <Text size={3}>Imports from Shop Api</Text>
            <Grid gridTemplateColumns={2} gap={[1, 1, 2, 3]}>
              <ImportButton
                icon={MdPriceCheck}
                text="Import Prices & Offers"
                description={`Use to re-import prices from the Shop Api`}
                disabled={buttonDisabled}
                onClick={() => handleRunImport("pricesAndOffers")}
              />

              <ImportButton
                icon={FaShip}
                text="Import Cruises"
                description={`Use to re-import "Cruise" data from the Shop Api`}
                disabled={buttonDisabled}
                onClick={() => handleRunImport("cruises")}
              />

              <ImportButton
                icon={FaHotel}
                text="Import Itinerary Shorex/LP/Hotels"
                description={`Use to re-import "Shorex", "Hotel" and "Land Programme"
                  data from the Shop Api`}
                disabled={buttonDisabled}
                onClick={() => handleRunImport("itinerary")}
              />

              <div style={{ gridColumn: "1/3" }}>
                <ImportButton
                  icon={GiDeathSkull}
                  tone="caution"
                  text="Import ALL (very long process)"
                  description={`WARNING: Use to re-import everything from the Shop Api`}
                  disabled={buttonDisabled}
                  onClick={() => handleRunImport("all")}
                />
              </div>
            </Grid>
          </Stack>
        </Card>
      </Stack>
    </DashboardWidgetContainer>
  );
};

export default InitiateImport;
